import { Body, Box, Heading, system } from '@hover/blueprint';
import { Flex, SimpleGrid } from '@hover/blueprint/chakra';
import React, { useContext, useEffect, useState } from 'react';

import { MenuHeader } from 'src/modules/designStudio/components/DesignMenu/Menu/MenuHeader';
import { WallMenu } from 'src/modules/designStudio/components/DesignMenu/Menu/WallMenu';
import { selectionStyles } from 'src/modules/designStudio/components/SelectionButton';
import { EmptyDesignConfig } from 'src/modules/designStudio/constants';
import { TextureColorGroupLabelColors } from 'src/modules/designStudio/constants/hardCodedData/textureData';
import { getNameOfLabel } from 'src/modules/designStudio/constants/productCatalog';
import type { TemplateWallAppearance } from 'src/modules/designStudio/helpers/data/TemplateDesignConfig';
import { createTemplateAppearanceFromUpdate } from 'src/modules/designStudio/helpers/data/TemplateDesignConfig';
import {
  MacheteBuildingContext,
  historyContext,
  productCatalogContext,
} from 'src/modules/designStudio/providers';
import { context } from 'src/modules/designStudio/providers/DesignStudioServiceProvider';
import type {
  LegacyDesignConfig,
  ProductEntry,
} from 'src/modules/designStudio/types';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import { buttonPressed } from 'src/shared/telemetry';

export const MaterialColorGroups = [
  { name: 'Beige', color: TextureColorGroupLabelColors.Beiges },
  { name: 'Blue', color: TextureColorGroupLabelColors.Blues },
  { name: 'Brown', color: TextureColorGroupLabelColors.Browns },
  { name: 'Grays', color: TextureColorGroupLabelColors.Grays },
  { name: 'Green', color: TextureColorGroupLabelColors.Greens },
  { name: 'Red', color: TextureColorGroupLabelColors.Reds },
  { name: 'Dark', color: TextureColorGroupLabelColors.Darks },
  { name: 'Mixed', color: TextureColorGroupLabelColors.Mixed },
];

export type Vector = { x: number; y: number; z: number };

export function hexToVector(color: string): Vector {
  const r = parseInt(`${color[1]}${color[2]}`, 16);
  const g = parseInt(`${color[3]}${color[4]}`, 16);
  const b = parseInt(`${color[5]}${color[6]}`, 16);
  return {
    x: r,
    y: g,
    z: b,
  };
}

const distance = (v: Vector, w: Vector): number =>
  Math.sqrt((v.x - w.x) ** 2 + (v.y - w.y) ** 2 + (v.z - w.z) ** 2);

export function getMaterialColorName(color: string): string {
  if (color.length !== 7) {
    return '';
  }

  const colorVector = hexToVector(color);
  const colorsGroups = MaterialColorGroups.map(group => ({
    name: group.name,
    vector: hexToVector(group.color),
  }));
  let closestColor: { name: string; vector: Vector } = colorsGroups[0];
  let closestDistance = distance(colorVector, closestColor.vector);

  colorsGroups.forEach(colorGroup => {
    const d = distance(colorGroup.vector, colorVector);
    if (d <= closestDistance) {
      closestDistance = d;
      closestColor = colorGroup;
    }
  });

  return closestColor.name;
}

const createDesignConfigForAppearanceGroup = (
  appearance: TemplateWallAppearance,
): LegacyDesignConfig => ({
  ...EmptyDesignConfig,
  WALLS: {
    textureId: appearance.textureId ?? null,
    color: appearance.color ?? null,
  },
});

type WallMaterialGroupsProps = {
  groups: TemplateWallAppearance[];
  onGroupSelected: (group: TemplateWallAppearance) => void;
};

const MaterialGroup = ({
  group,
  onClick,
  testId,
}: {
  group: TemplateWallAppearance;
  onClick: () => void;
  testId: string;
}) => {
  const { historyState } = useContext(historyContext);
  const { appService } = useContext(context);
  const { textures } = useContext(productCatalogContext);
  const texture: ProductEntry = textures?.find(
    t => t.id === group.textureId,
  ) as ProductEntry;

  const materialName = getNameOfLabel(texture?.label_id ?? 0);
  // TODO: https://hoverinc.atlassian.net/browse/TDP-5310 Pull color from colors group graphQL query
  const colorName = getMaterialColorName(group.color ?? '#ffffff');
  const thumbnail = texture?.texture?.thumbnail.url;

  return (
    <Flex
      data-testid={testId}
      flexDirection="column"
      grow="1"
      onClick={() => {
        onClick();
        const trackingData = {
          feature: 'Wall Group',
          button_text: materialName,
          user_email: historyState.email ?? undefined,
          job_id: historyState.jobId ?? undefined,
          page_or_screen_name: 'Design-studio',
          primary_cta: false,
          org_id: appService.getSnapshot().context.orgId,
          org_name: appService.getSnapshot().context.requestingOrgName,
        };
        buttonPressed(trackingData);
      }}
      shrink="0"
      width={{ base: '158px', tablet: 'auto' }}
    >
      <system.button
        {...selectionStyles({ isSelected: false })}
        borderRadius={300}
        cursor="pointer"
        height="107px"
        onClick={onClick}
        position="relative"
        width="100%"
      >
        <Box
          backgroundBlendMode={thumbnail ? 'multiply' : undefined}
          backgroundColor={group.color ?? '#ffffff'}
          backgroundImage={thumbnail ?? undefined}
          borderRadius="inherit"
          height="100%"
          width="100%"
        />
      </system.button>
      <Box display="block">
        <Body
          fontSize={200}
          fontWeight={600}
          lineHeight={200}
          marginBottom={0}
          marginTop={200}
        >
          {materialName}
        </Body>
        {colorName && (
          <Body
            color="neutral.600"
            fontSize={100}
            fontWeight={400}
            lineHeight={100}
            m={0}
          >
            {colorName}
          </Body>
        )}
      </Box>
    </Flex>
  );
};

const WallMaterialGroups = ({
  groups,
  onGroupSelected,
}: WallMaterialGroupsProps) => {
  const t = useTranslate(TranslationNamespace.DESIGN_STUDIO);
  return (
    <>
      <Flex flexDirection="row">
        <Heading
          color="neutral.900"
          fontSize="16px"
          fontWeight={700}
          marginBottom={0}
        >
          {t('menu.wallGroup.title')}
        </Heading>
        <Body
          fontSize={100}
          fontWeight={600}
          marginBottom={0}
          marginLeft="auto"
        >{`${groups.length} items`}</Body>
      </Flex>
      <SimpleGrid
        boxSizing="border-box"
        columns={2}
        display={{ base: 'flex', tablet: 'grid' }}
        gap={300}
        overflowX={{ base: 'scroll', tablet: 'auto' }}
        overflowY="visible"
        padding="4px"
        paddingBottom={{ base: '16px', tablet: '0px' }}
        width="100%"
      >
        {groups.map((group, index) => (
          <MaterialGroup
            group={group}
            key={`${group.textureId ?? ''}${group.color ?? ''}`}
            onClick={() => {
              onGroupSelected(group);
            }}
            testId={`group${index}`}
          />
        ))}
      </SimpleGrid>
    </>
  );
};

export const WallGroupMenu = ({ closeMenu }: { closeMenu: () => void }) => {
  const t = useTranslate(TranslationNamespace.DESIGN_STUDIO);
  const headerText = t('menu.wall.title') ?? '';
  const { updateDesign } = useContext(historyContext);
  const { building } = useContext(MacheteBuildingContext);
  const [selectedWallAppearance, setSelectedWallAppearance] = useState<
    TemplateWallAppearance | undefined
  >(undefined);

  const wallAppearances = building!.designer
    .get_active_design()
    .get_wall_appearances();

  const modelHasMoreThanOneGroup = wallAppearances.length > 1;

  // If model has one group, we select it here
  useEffect(() => {
    if (wallAppearances.length === 1) {
      setSelectedWallAppearance(wallAppearances[0]);
    }
  }, [wallAppearances]);

  const showWallGroupsMenu =
    modelHasMoreThanOneGroup && selectedWallAppearance === undefined;

  if (showWallGroupsMenu) {
    return (
      <>
        <MenuHeader closeMenu={closeMenu} headerText={headerText} />
        <WallMaterialGroups
          groups={wallAppearances}
          onGroupSelected={groupSelected => {
            setSelectedWallAppearance(groupSelected);
          }}
        />
      </>
    );
  }

  let onBack;
  if (modelHasMoreThanOneGroup) {
    onBack = () => {
      setSelectedWallAppearance(undefined);
    };
  }

  return (
    <WallMenu
      closeMenu={closeMenu}
      designConfig={createDesignConfigForAppearanceGroup(
        selectedWallAppearance ?? wallAppearances[0],
      )}
      headerText={headerText}
      onBack={onBack}
      updateDesign={update => {
        // See DS-846
        // eslint-disable-next-line no-param-reassign
        update.update.targetAppearanceGroup = { ...selectedWallAppearance };
        setSelectedWallAppearance(
          createTemplateAppearanceFromUpdate(
            update.category,
            update.update,
          ) as TemplateWallAppearance,
        );
        updateDesign(update);
      }}
    />
  );
};
